import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { getLoginRedirectUrl } from '@/lib/auth-api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { MAIN_SITE_URL } from '@/lib/config'
import { ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react'

type Step = 'credentials' | '2fa'

export function LoginPage() {
  const { token, isStaff, loading, login, verify2FA } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [tempToken, setTempToken] = useState<string | null>(null)
  const [step, setStep] = useState<Step>('credentials')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [searchParams] = useSearchParams()
  const next = searchParams.get('next') || '/'

  useEffect(() => {
    if (!loading && token && isStaff) navigate(next, { replace: true })
  }, [loading, token, isStaff, navigate, next])

  if (!loading && token && isStaff) return <Navigate to={next} replace />

  async function onSubmitCredentials(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const result = await login(email, password)
      if (result.status === '2fa') {
        setTempToken(result.tempToken)
        setStep('2fa')
        setTotpCode('')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function onSubmit2FA(e: FormEvent) {
    e.preventDefault()
    if (!tempToken) {
      setError('Session expired. Please sign in again.')
      setStep('credentials')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await verify2FA(tempToken, totpCode)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="admin-login-shell">
      <aside className="admin-login-context">
        <a href={MAIN_SITE_URL} className="admin-login-brand"><span>c</span><strong>clyr</strong></a>
        <div>
          <span className="admin-login-kicker">Staff only</span>
          <h1>One place to run clyr.</h1>
          <p>Review safety reports, manage the platform, publish updates, and keep an auditable record of every decision.</p>
        </div>
        <ul>
          <li><ShieldCheck aria-hidden="true" /><span><strong>Role-based access</strong><small>Only the tools assigned to your staff role appear.</small></span></li>
          <li><KeyRound aria-hidden="true" /><span><strong>Two-factor protected</strong><small>Administrative sessions support authenticator verification.</small></span></li>
        </ul>
        <a href={MAIN_SITE_URL} className="admin-login-back"><ArrowLeft aria-hidden="true" /> Back to clyr</a>
      </aside>

      <main className="admin-login-main">
        <div className="admin-login-card">
          <header>
            <span className="admin-login-mobile-mark">c</span>
            <div>
              <h2>{step === 'credentials' ? 'Sign in to the control room' : 'Confirm it’s you'}</h2>
              <p>{step === 'credentials' ? 'Use your existing clyr staff account.' : 'Enter the code from your authenticator app.'}</p>
            </div>
          </header>
          {step === 'credentials' ? (
            <form onSubmit={onSubmitCredentials} className="space-y-5">
              <div>
                <label htmlFor="email">Email address</label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@clyrzones.com"
                />
              </div>
              <div>
                <label htmlFor="password">Password</label>
                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-[#fca5a5]" role="alert">{error}</p>}
              {!loading && token && !isStaff && (
                <p className="text-sm text-[#fcd34d]" role="status">
                  Signed in, but this account does not have staff permissions.
                </p>
              )}
              <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          ) : (
            <form onSubmit={onSubmit2FA} className="space-y-5">
              <Input
                id="totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center tracking-[0.35em] font-mono text-lg"
              />
              {error && <p className="text-sm text-[#fca5a5]" role="alert">{error}</p>}
              <Button type="submit" className="w-full" size="lg" disabled={submitting || totpCode.length !== 6}>
                {submitting ? 'Verifying…' : 'Verify and sign in'}
              </Button>
              <button
                type="button"
                className="w-full text-xs text-[#93a5ad] hover:text-[#40dbf1] transition-colors"
                onClick={() => {
                  setStep('credentials')
                  setTempToken(null)
                  setTotpCode('')
                  setError(null)
                }}
              >
                ← Back to sign in
              </button>
            </form>
          )}
          <div className="admin-login-divider"><span>or</span></div>
          <a href={getLoginRedirectUrl()} className="text-[#40dbf1] hover:text-[#7aebfa] font-medium">
            Continue through clyrzones.com
          </a>
        </div>
      </main>
    </div>
  )
}
